/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useMemo, useState } from 'react';
import logger from '@/logger';
import OnlineDegreeDescriptionPage from '@/components/OnlineDegreeDescriptionPage';
import CourseDescriptionContext from '@/context/CourseDescriptionContext';
import { ICourseDescription } from '@/interfaces/courseDescription';
import { companyImage, domainUrl } from '@/utils/common/env.constants';
import RouteName, { getCanonicalUrl } from '@/utils/route.util';
import Head from 'next/head';
import { IFaqsDetails } from '@/interfaces/homePage';
import CourseDescription from '@/api/courseDescription';
import { CourseStatusEnums } from '@/components/PostLoginOnboarding/Utilities/enums';
import moEvent from '@/utils/moEngage';
import { useRouter } from 'next/router';
import Spinner from '@/components/common/Loader';
import OnlineDegreePremiumLayout from '@/components/common/OnlineDegreePremiumLayout';
import loadCommonAdmitadScript from '@/utils/common/common.admitAd';
import { cacheCourseBatchMetaFromCourseDetails } from '@/utils/courseBatchMetaCache';
import ErrorPage from '../404';
import useUserDetails from '../../store/userDetails';

const DegreePage = ({ response, slug }: { response: any; slug: string }) => {
  const [courseDetails, setCourseDetails] = useState<ICourseDescription | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const { isLoggedIn, user } = useContext(UserDetailsContext);
  const { userDetails: user, userIsLoggedIn: isLoggedIn } = useUserDetails();
  const router = useRouter();
  const [isVerifyingSubDomain, setIsVerifyingSubDomain] = useState(true);

  const useStore = useMemo(() => {
    return {
      courseDetails,
      setCourseDetails,
      loading: isLoading,
      setIsLoading,
      lessonId: '',
      mappedCourseDetails: {
        courseId: '',
        courseSlug: '',
      },
      isCourseBought: false,
    };
  }, [courseDetails, isLoading]);

  const checkValidSubDomain = () => {
    if (typeof window !== 'undefined') {
      // Get the hostname (e.g., 'subdomain.example.com')
      const { hostname } = window.location;
      const domainParts = hostname.split('.');
      let currentSubdomain = '';

      // Extract the subdomain if available
      if (domainParts.length > 2) {
        currentSubdomain = domainParts.slice(0, domainParts.length - 2).join('.');
        if (courseDetails?.partnerInstituteDetails && currentSubdomain) {
          if (currentSubdomain === courseDetails?.partnerInstituteDetails[0]?.subDomain) {
            setIsVerifyingSubDomain(false);
          } else {
            router.replace(`${domainUrl}/online-degree`);
          }
        } else {
          router.replace(`${domainUrl}/online-degree`);
        }
      }
    }
  };

  useEffect(() => {
    if (slug) {
      setCourseDetails(response.data);
      cacheCourseBatchMetaFromCourseDetails(response?.data);
    }
  }, [slug]);

  useEffect(() => {
    if (courseDetails?.isPremiumOnlineDegreeCourse) {
      checkValidSubDomain();
    }
  }, [courseDetails]);

  // Get the canonical URL based on the value from the query or any other source
  const canonicalUrl = getCanonicalUrl(response.data._id);

  // moEngage Event
  useEffect(() => {
    if (courseDetails?._id && router?.isReady && user?._id) {
      const moEventData = {
        user_id: user?._id || '',
        username: `${user?.firstName} ${user?.lastName}` || '',
        phone_number: user?.phoneNumber || '',
        user_type: isLoggedIn ? 'loggedin' : 'not_loggedin',
        course_name: courseDetails?.name || courseDetails?.title || '',
        course_id: courseDetails?._id || '',
        course_type: courseDetails.courseType || '',
        course_slug: courseDetails?.slug || '',
        ...(user?.verified && user?.email && { email: user.email }),
      };
      moEvent('online_degree_description_visit', moEventData);
    }
  }, [courseDetails?._id, user, router?.isReady]);

  useEffect(() => {
    if (router.isReady) {
      loadCommonAdmitadScript();
    }
  }, [router.isReady]);

  const structData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: response?.data?.faq?.map((faq: IFaqsDetails) => {
      return {
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      };
    }),
  };

  const strucDataBreadCrumb = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://pwskills.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${response?.data?.name}`,
          item: `https://pwskills.com/courses/${response?.data?.slug}`,
        },
      ],
    },
  ];

  const structDataVideo = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: response?.data?.name,
    description: response?.data?.shortDescription,
    // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
    thumbnailUrl:
      response?.data?.img && response?.data?.img.startsWith('https')
        ? response?.data?.img
        : `${companyImage}/thumbnails/${response?.data?.img}`,
    uploadDate: response?.data?.registration?.startDate,
    duration: '',
    contentUrl: response?.data?.videoURL,
    embedUrl: response?.data?.videoURL,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: '',
    },
    regionsAllowed: 'IN,US',
  };

  const fetchStartDate = () => {
    const istFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata' });
    const istDate =
      response?.data?.classTimings?.startDate &&
      istFormatter?.format(new Date(response?.data?.classTimings?.startDate));
    return istDate;
  };

  const structDataEducationEvent = {
    '@context': 'http://schema.org',
    '@type': 'EducationEvent',
    name: response?.data?.name,
    description: response?.data?.shortDescription,
    image: response?.data?.img,
    performer: 'PW Skills',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: Math.floor(
        (response?.data?.pricing?.IN || 0) -
          (Math.abs(response?.data?.pricing?.discount || 0) * (response?.data?.pricing?.IN || 0)) /
            100
      ),
      highPrice: response?.data?.pricing?.IN || 0,
      url: `https://pwskills.com/course/${response?.data?.slug}/`,
      availability: 'https://schema.org/InStock',
      price: Math.floor(
        (response?.data?.pricing?.IN || 0) -
          (Math.abs(response?.data?.pricing?.discount || 0) * (response?.data?.pricing?.IN || 0)) /
            100
      ),
      priceCurrency: 'INR',
      validFrom: '',
    },
    startDate: fetchStartDate(),
    endDate: response?.data?.registration?.endDate,
    url: `https://pwskills.com/course/${response?.data?.slug}/`,
    duration: response?.data?.keyValueMetaInfo?.Duration || '',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'http://schema.org/EventScheduled',
    location: {
      '@type': 'VirtualLocation',
      url: `https://pwskills.com/course/${response?.data?.slug}/`,
    },
    organizer: {
      '@type': 'Organization',
      name: 'PW Skills',
      url: 'https://pwskills.com/',
    },
  };

  const structCourseList = {
    '@id': '',
    '@context': 'https://schema.org',
    type: 'Course',
    name: `${response?.data?.title}`,
    description: `${response?.data?.seo?.description}`,
    provider: {
      '@type': 'Organization',
      name: 'PW Skills',
      sameAs: `https://pwskills.com/course/${response?.data?.slug}`,
    },
    offers: [
      {
        '@type': 'Offer',
        category: 'Paid',
        priceCurrency: 'INR',
        price: Math.floor(
          (response?.data?.pricing?.IN || 0) -
            (Math.abs(response?.data?.pricing?.discount || 0) *
              (response?.data?.pricing?.IN || 0)) /
              100
        ),
      },
    ],
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: 'P2D',
      },
    ],
  };

  return (
    <>
      <Head>
        {/* Primary Meta Tags */}
        <title>{response?.data?.seo?.title}</title>
        <meta name="title" content={response?.data?.seo?.title} />
        <meta name="description" content={response?.data?.seo?.description} />

        {/* Canonical url is added only for the listed courses in canonicalMap */}
        <link rel="canonical" href={canonicalUrl || `${RouteName.degree}/${slug}/`} />

        {/* Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${RouteName.degree}/${slug}`} />
        <meta
          property="og:image"
          content={
            response?.data?.seo?.ogImage && response?.data?.seo?.ogImage.trim() !== ''
              ? response?.data?.seo?.ogImage
              : // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
              response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img}`
          }
        />
        <meta
          property="og:title"
          content={
            response?.data?.seo?.ogTitle ? response?.data?.seo?.ogTitle : response?.data?.seo?.title
          }
        />
        <meta
          property="og:description"
          content={
            response?.data?.seo?.ogDescription
              ? response?.data?.seo?.ogDescription
              : response?.data?.seo?.description
          }
        />
        <meta property="og:image:width" content="456" />
        <meta property="og:image:height" content="256" />

        {/* Twitter */}
        <meta property="twitter:url" content={`${RouteName.degree}/${slug}`} />
        <meta
          property="twitter:title"
          content={
            response?.data?.seo?.twitterTitle
              ? response?.data?.seo?.twitterTitle
              : response?.data?.seo?.title
          }
        />
        <meta
          property="twitter:description"
          content={
            response?.data?.seo?.twitterDescription
              ? response?.data?.seo?.twitterDescription
              : response?.data?.seo?.description
          }
        />
        <meta
          property="twitter:image"
          content={
            response?.data?.seo?.twitterImage && response?.data?.seo?.twitterImage.trim() !== ''
              ? response?.data?.seo?.twitterImage
              : // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
              response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img}`
          }
        />
        <meta property="twitter:image:width" content="456" />
        <meta property="twitter:image:height" content="256" />
        <script
          key="course-description-faqs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structData) }}
        />
        <script
          key="course-description-breadcrumbs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(strucDataBreadCrumb) }}
        />
        <script
          key="course-description-video-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataVideo) }}
        />
        <script
          key="course-description-education-event"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataEducationEvent) }}
        />
        <script
          key="course-description-course-list"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structCourseList) }}
        />
      </Head>
      {/* description page */}
      {courseDetails ? (
        courseDetails?.status === CourseStatusEnums.ACTIVE ? (
          <CourseDescriptionContext.Provider value={useStore}>
            {courseDetails?.isPremiumOnlineDegreeCourse ? (
              isVerifyingSubDomain ? (
                <Spinner />
              ) : (
                <OnlineDegreePremiumLayout>
                  <OnlineDegreeDescriptionPage />
                </OnlineDegreePremiumLayout>
              )
            ) : (
              <OnlineDegreePremiumLayout>
                <OnlineDegreeDescriptionPage />
              </OnlineDegreePremiumLayout>
            )}
          </CourseDescriptionContext.Provider>
        ) : (
          <ErrorPage />
        )
      ) : (
        <Spinner />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export async function getStaticProps({ params }: { params: { degreeSlug: string } }) {
  try {
    const response = await CourseDescription.getCourseDescription(params.degreeSlug);
    logger.info(`GET /degree/${params.degreeSlug}`);
    return {
      props: {
        slug: params.degreeSlug,
        response,
      },
      revalidate: 10,
    };
  } catch (error: any) {
    logger.error(`GET /degree/${params.degreeSlug} ${error.error ? error.error : error}`);
    return { notFound: true, revalidate: 1 };
  }
}

export default DegreePage;
